import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaLinkedin,
  FaTwitter,
  FaComments,
  FaComment,
  FaArrowUp,
  FaRocket,
  FaChartLine,
  FaUsers,
  FaAward,
} from "react-icons/fa";

const FooterSection = styled.footer`
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(102, 126, 234, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(118, 75, 162, 0.03) 0%,
      transparent 50%
    );
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 80px 30px 40px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 60px 20px 30px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const CompanyInfo = styled.div`
  color: white;
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
`;

const LogoIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 20px;
`;

const LogoText = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CompanyDescription = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
  margin-bottom: 30px;
  font-size: 1rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #b8b8b8;
  font-size: 0.95rem;
  transition: color 0.3s ease;

  &:hover {
    color: #667eea;
  }
`;

const ContactIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  flex-shrink: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled(motion.a)`
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8b8b8;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
  }
`;

const FooterColumn = styled.div`
  color: white;
`;

const ColumnTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 25px;
  color: #ffffff;
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FooterLink = styled(motion.a)`
  color: #b8b8b8;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #667eea;
    transform: translateX(5px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 20px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #667eea;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #b8b8b8;
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #b8b8b8;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const Copyright = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackToTop = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
  }
`;

const Footer = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <FooterSection ref={ref}>
      <BackgroundPattern />
      <Container>
        <FooterContent>
          <CompanyInfo>
            <Logo
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <LogoIcon>233</LogoIcon>
              <LogoText>深圳二三三数字科技</LogoText>
            </Logo>

            <CompanyDescription
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              成立于2014年，专注于为企业提供专业的数字营销服务。
              我们致力于帮助客户在数字世界中实现品牌增长和业务成功。
            </CompanyDescription>

            <ContactInfo
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ContactItem>
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                深圳市南山区招商街道蛇口南海大道1031号万海大厦C座201室
              </ContactItem>

              <ContactItem>
                <ContactIcon>
                  <FaPhone />
                </ContactIcon>
                400-888-9999
              </ContactItem>

              <ContactItem>
                <ContactIcon>
                  <FaEnvelope />
                </ContactIcon>
                kun@ersansan.cn
              </ContactItem>

              <ContactItem>
                <ContactIcon>
                  <FaClock />
                </ContactIcon>
                周一至周五 9:00-18:00
              </ContactItem>
            </ContactInfo>

            <SocialLinks
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaComments />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaComment />
              </SocialLink>
            </SocialLinks>
          </CompanyInfo>

          <FooterColumn>
            <ColumnTitle
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              服务项目
            </ColumnTitle>
            <FooterLinks
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FooterLink
                onClick={() => scrollToSection("services")}
                whileHover={{ x: 5 }}
              >
                <FaRocket />
                数字广告投放
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("services")}
                whileHover={{ x: 5 }}
              >
                <FaChartLine />
                数据分析服务
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("services")}
                whileHover={{ x: 5 }}
              >
                <FaUsers />
                社交媒体营销
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("services")}
                whileHover={{ x: 5 }}
              >
                <FaAward />
                SEO优化服务
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              快速导航
            </ColumnTitle>
            <FooterLinks
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <FooterLink
                onClick={() => scrollToSection("hero")}
                whileHover={{ x: 5 }}
              >
                首页
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("about")}
                whileHover={{ x: 5 }}
              >
                关于我们
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("portfolio")}
                whileHover={{ x: 5 }}
              >
                成功案例
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("team")}
                whileHover={{ x: 5 }}
              >
                专业团队
              </FooterLink>
              <FooterLink
                onClick={() => scrollToSection("contact")}
                whileHover={{ x: 5 }}
              >
                联系我们
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              公司实力
            </ColumnTitle>
            <StatsGrid
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <StatItem>
                <StatNumber>1000+</StatNumber>
                <StatLabel>合作客户</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>500+</StatNumber>
                <StatLabel>成功案例</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>300%</StatNumber>
                <StatLabel>ROI提升</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>10年</StatNumber>
                <StatLabel>行业经验</StatLabel>
              </StatItem>
            </StatsGrid>
          </FooterColumn>
        </FooterContent>

        <FooterBottom
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Copyright>
            © {currentYear} 深圳二三三数字科技有限公司. 保留所有权利.
          </Copyright>

          <BackToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaArrowUp />
          </BackToTop>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;
